import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Typography, Box } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ReactCountryFlag from 'react-country-flag';

// SVG images: emoji flags are not rendered on Windows (they show as two letters).
const Flag = ({ country, size = '1.2em' }) => (
  <ReactCountryFlag countryCode={country} svg aria-hidden="true" style={{ width: size, height: size, borderRadius: 3, objectFit: 'cover' }} />
);

export const languages = [
  { code: 'en', name: 'English', country: 'GB' },
  { code: 'it', name: 'Italiano', country: 'IT' },
  { code: 'es', name: 'Español', country: 'ES' },
  { code: 'de', name: 'Deutsch', country: 'DE' },
  { code: 'fr', name: 'Français', country: 'FR' },
  { code: 'pt', name: 'Português', country: 'PT' },
  { code: 'ja', name: '日本語', country: 'JP' },
  { code: 'zh', name: '中文', country: 'CN' },
  { code: 'nl', name: 'Nederlands', country: 'NL' },
  { code: 'pl', name: 'Polski', country: 'PL' },
  { code: 'ru', name: 'Русский', country: 'RU' },
  { code: 'hi', name: 'हिन्दी', country: 'IN' }
];

export default function LanguageSelector({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    try {
      document.documentElement.lang = code;
      document.dir = 'ltr';
    } catch { /* ignore */ }
    handleClose();
  };

  const currentLang = languages.find(l => l.code === (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]) || languages[0];

  return (
    <Box translate="no" className="notranslate">
      <Tooltip title={t('common.language')}>
        {compact ? (
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-controls={open ? 'language-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label={t('common.language')}
          >
            <Flag country={currentLang.country} size="1.35rem" />
          </IconButton>
        ) : (
          <Button
            id="language-button"
            aria-controls={open ? 'language-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              color: 'text.primary',
              fontWeight: 700,
              px: 1.3,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#FFF',
              '&:hover': { backgroundColor: 'rgba(79,70,229,0.06)', borderColor: 'primary.light' },
            }}
          >
            <Box component="span" sx={{ mr: 1, display: 'inline-flex' }}><Flag country={currentLang.country} /></Box>
            <Typography component="span" variant="body2" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
              {currentLang.code}
            </Typography>
          </Button>
        )}
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, maxHeight: 'min(420px, 70vh)', minWidth: 200 } }}
      >
        {languages.map((lang) => {
          const selected = currentLang.code === lang.code;
          return (
            <MenuItem
              key={lang.code}
              selected={selected}
              onClick={() => handleLanguageChange(lang.code)}
              translate="no"
              className="notranslate"
              sx={{ py: 1.1, mx: 0.75, my: 0.25, borderRadius: '10px' }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}><Flag country={lang.country} size="1.3rem" /></ListItemIcon>
              <ListItemText primary={lang.name} primaryTypographyProps={{ fontWeight: selected ? 800 : 500 }} />
              {selected && <CheckRoundedIcon fontSize="small" color="primary" />}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
